/**
 * A button that opens a small menu below it: the header's "Ações" and the avatar's menu. Closes on a
 * click outside, on Esc, and after an item is chosen (an item that wants the menu open stops it).
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

export function Dropdown({
  button,
  label,
  className = '',
  buttonClassName = 'btn btn--sm',
  align = 'end',
  children,
}: {
  button: ReactNode
  label: string
  className?: string
  buttonClassName?: string
  align?: 'start' | 'end'
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  // The side it opens to: the one asked for, unless that side would be cut off by whatever clips it (a panel that
  // scrolls, the window) — then the other one.
  const [side, setSide] = useState(align)
  const flipped = useRef(false)
  useLayoutEffect(() => {
    if (!open) {
      flipped.current = false
      setSide(align)
      return
    }
    // Once per opening: with no room on either side it stays where it first went.
    if (flipped.current) return
    const box = menu.current?.getBoundingClientRect()
    if (!box) return
    let left = 0
    let right = window.innerWidth
    for (let node = menu.current?.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node)
      if (style.overflowX !== 'visible' || style.overflow !== 'visible') {
        const clip = node.getBoundingClientRect()
        left = Math.max(left, clip.left)
        right = Math.min(right, clip.right)
      }
    }
    const other = side === 'end' && box.left < left ? 'start' : side === 'start' && box.right > right ? 'end' : null
    if (other) {
      flipped.current = true
      setSide(other)
    }
  }, [open, align, side])

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <div className={`dropdown ${className}`} ref={ref}>
      <button type="button" className={buttonClassName} aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen(!open)}>
        {button}
      </button>
      {open && (
        <div ref={menu} className={`dropdown__menu dropdown__menu--${side}`} role="menu" aria-label={label}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

/** One line of a dropdown: an icon, a label, and on the right what it is doing or a shortcut. */
export function DropdownItem({ icon, label, hint, side, onSelect, disabled = false, danger = false }: {
  icon?: ReactNode
  label: string
  hint?: string
  side?: ReactNode
  onSelect: () => void
  disabled?: boolean
  danger?: boolean
}) {
  return (
    <button type="button" role="menuitem" className={`dropdown__item${danger ? ' is-danger' : ''}`} onClick={onSelect} disabled={disabled} title={hint}>
      {icon}
      <span className="dropdown__label">{label}</span>
      {side && <span className="dropdown__side tiny muted">{side}</span>}
    </button>
  )
}
